import { useMemo, useState } from 'react'

// Constant for the ellipsis marker
export const PAGINATION_DOTS = '...'

// Helper function to generate a range of numbers (inclusive)
function range(start: number, end: number): number[] {
  return Array.from({ length: end - start + 1 }, (_, idx) => idx + start)
}

// Props for the hook
export interface UsePaginationProps {
  /**
   * The initial page number. If not provided, it defaults to 1.
   */
  currentPage?: number
  /**
   * Total number of items available.
   * If not provided, it defaults to 0.
   */
  total?: number
  /**
   * The number of sibling pages to show on each side of the current page.
   * Defaults to 1.
   */
  siblingCount?: number
  /**
   * Number of items per page. Defaults to 10.
   */
  pageSize?: number
}

// Define a type for the button properties
export interface ButtonProps {
  onClick: () => void
  disabled: boolean
}

// The hook's return type
export interface UsePaginationResult {
  // Current page state managed internally by the hook.
  currentPage: number
  // Array of page numbers and/or ellipsis markers to render.
  paginationRange: (number | string)[]
  // Indicates if there is a previous page.
  hasPrevious: boolean
  // Indicates if there is a next page.
  hasNext: boolean
  // Function to navigate to the next page.
  nextPage: () => void
  // Function to navigate to the previous page.
  previousPage: () => void
  // Function to jump to a specific page.
  goToPage: (page: number) => void
  // Number of items to skip (used in API requests).
  skip: number
  // Limit (number of items per page, used in API requests).
  limit: number
  // Returns props for the "previous" button.
  getPrevButtonProps: () => ButtonProps
  // Returns props for the "next" button.
  getNextButtonProps: () => ButtonProps
  // Returns props for a "go to page" button.
  getGoToPageButtonProps: (pageNum: number) => ButtonProps
  // Function to update the total number of items
  setTotal: (total: number) => void
}

/**
 * Custom hook for pagination.
 *
 * @param {UsePaginationProps} params - The hook parameters.
 * @returns {UsePaginationResult} An object with pagination state and helper methods.
 */
export function usePagination({
  currentPage = 1,
  total = 0,
  siblingCount = 1,
  pageSize = 10,
}: UsePaginationProps): UsePaginationResult {
  // Internal state for the current page (defaults to the provided currentPage or 1)
  const [current, setCurrent] = useState<number>(currentPage)
  // Internal state for the total number of items; allows later updates via setTotal.
  const [currentTotal, setTotal] = useState<number>(total)

  // Compute the total pages based on the current total number of items and page size.
  const totalPages = Math.ceil(currentTotal / pageSize)

  // Booleans to determine if navigation buttons should be enabled or disabled.
  const hasPrevious = current > 1
  const hasNext = current < totalPages

  // Function to navigate to the next page.
  const nextPage = () => {
    if (hasNext) {
      setCurrent(current + 1)
    }
  }

  // Function to navigate to the previous page.
  const previousPage = () => {
    if (hasPrevious) {
      setCurrent(current - 1)
    }
  }

  // Function to jump to a specific page.
  const goToPage = (page: number) => {
    // Clamp the page number between 1 and totalPages.
    const newPage = Math.max(1, Math.min(page, totalPages))
    if (newPage !== current) {
      setCurrent(newPage)
    }
  }

  // Memoize the pagination range to avoid unnecessary recalculations.
  const paginationRange = useMemo<(number | string)[]>(() => {
    // Return an empty array if there are no pages.
    if (totalPages === 0) return []

    // Total number of pagination buttons we aim to show:
    // first page, last page, current page, sibling pages, and two DOTS.
    const totalPageNumbers = siblingCount * 2 + 5

    // If the number of pages is less than the pagination numbers we want to show, return full range.
    if (totalPageNumbers >= totalPages) {
      return range(1, totalPages)
    }

    // Determine the left and right sibling index boundaries.
    const leftSiblingIndex = Math.max(current - siblingCount, 1)
    const rightSiblingIndex = Math.min(current + siblingCount, totalPages)

    const shouldShowLeftDots = leftSiblingIndex > 2
    const shouldShowRightDots = rightSiblingIndex < totalPages - 1

    // Case 1: No left dots but right dots should be shown.
    if (!shouldShowLeftDots && shouldShowRightDots) {
      const leftItemCount = 3 + 2 * siblingCount
      const leftRange = range(1, leftItemCount)
      return [...leftRange, PAGINATION_DOTS, totalPages]
    }

    // Case 2: Left dots should be shown but no right dots.
    if (shouldShowLeftDots && !shouldShowRightDots) {
      const rightItemCount = 3 + 2 * siblingCount
      const rightRange = range(totalPages - rightItemCount + 1, totalPages)
      return [1, PAGINATION_DOTS, ...rightRange]
    }

    // Case 3: Both left and right dots should be shown.
    if (shouldShowLeftDots && shouldShowRightDots) {
      const middleRange = range(leftSiblingIndex, rightSiblingIndex)
      return [1, PAGINATION_DOTS, ...middleRange, PAGINATION_DOTS, totalPages]
    }

    // Fallback (should not normally be reached).
    return []
  }, [current, totalPages, siblingCount])

  // Compute API request properties.
  const skip = (current - 1) * pageSize
  const limit = pageSize

  // Helper function to get props for the previous button.
  const getPrevButtonProps = (): ButtonProps => ({
    onClick: () => {
      if (hasPrevious) {
        setCurrent(current - 1)
      }
    },
    disabled: !hasPrevious,
  })

  // Helper function to get props for the next button.
  const getNextButtonProps = (): ButtonProps => ({
    onClick: () => {
      if (hasNext) {
        setCurrent(current + 1)
      }
    },
    disabled: !hasNext,
  })

  // Helper function to get props for a "go to page" button.
  // The button is disabled if the provided page is out of range or is the current page.
  const getGoToPageButtonProps = (pageNum: number): ButtonProps => ({
    onClick: () => {
      if (pageNum >= 1 && pageNum <= totalPages && pageNum !== current) {
        setCurrent(pageNum)
      }
    },
    disabled: pageNum < 1 || pageNum > totalPages || pageNum === current,
  })

  return {
    currentPage: current,
    paginationRange,
    hasPrevious,
    hasNext,
    nextPage,
    previousPage,
    goToPage,
    skip,
    limit,
    getPrevButtonProps,
    getNextButtonProps,
    getGoToPageButtonProps,
    setTotal,
  }
}
